import React, { useState } from "react";
import { Modal } from "flowbite-react";
import { IoMdAdd, IoMdTrash, IoMdClose } from "react-icons/io";
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "../config/axiosInstance";
import { MONITORED_CLIENT } from "../config/apiConfig";
import { toast } from "react-toastify";
import { useApplication } from "../contexts/ApplicationContext";
import {
  MdRadar,
  MdVerifiedUser,
  MdNotificationsActive,
  MdTrendingUp,
  MdPersonSearch,
  MdHistory,
  MdAccountBalanceWallet,
  MdGavel,
  MdMoneyOff,
  MdCreditScore,
  MdNotificationImportant,
  MdWarningAmber,
  MdManageAccounts,
  MdEmail,
} from "react-icons/md";
import { maskCPF, maskCNPJ } from "../utils/masks";
import "./MonitoreClientesPage.css";

const BENEFITS = [
  {
    icon: MdRadar,
    title: "Monitoramento Contínuo",
    desc: "Acompanhe seus clientes em tempo real e seja avisado sobre qualquer alteração no perfil de crédito ou cadastral.",
    color: "blue",
  },
  {
    icon: MdNotificationsActive,
    title: "Alertas Instantâneos",
    desc: "Receba notificações imediatas quando surgir uma nova negativação, protesto ou ocorrência relevante.",
    color: "amber",
  },
  {
    icon: MdVerifiedUser,
    title: "Gestão de Risco Proativa",
    desc: "Antecipe inadimplências e tome decisões mais seguras antes que problemas afetem o seu negócio.",
    color: "emerald",
  },
  {
    icon: MdTrendingUp,
    title: "Inteligência Estratégica",
    desc: "Utilize os dados monitorados para criar estratégias de cobrança e crédito mais eficientes e personalizadas.",
    color: "violet",
  },
];

function applyDocMask(value) {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 11) return maskCPF(digits);
  return maskCNPJ(digits.slice(0, 14));
}

function validateCPF(cpf) {
  const d = cpf.replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(d[i]) * (10 - i);
  let r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  if (r !== Number(d[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += Number(d[i]) * (11 - i);
  r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  return r === Number(d[10]);
}

function validateCNPJ(cnpj) {
  const d = cnpj.replace(/\D/g, "");
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const calc = (d, len) => {
    let sum = 0, pos = len - 7;
    for (let i = len; i >= 1; i--) {
      sum += Number(d[len - i]) * pos--;
      if (pos < 2) pos = 9;
    }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return calc(d, 12) === Number(d[12]) && calc(d, 13) === Number(d[13]);
}

function validateDoc(digits) {
  if (digits.length === 11) return validateCPF(digits);
  if (digits.length === 14) return validateCNPJ(digits);
  return false;
}

function hasTruthyFlag(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function HistoryModal({ client, onClose }) {
  const { data, isLoading } = useQuery(
    ["monitored-client-history-user", client.id],
    async () => {
      const { data } = await axios.get(MONITORED_CLIENT.GET_HISTORY_USER(client.id));
      return data.data;
    }
  );

  const entries = data || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl mx-4 flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between p-3 border-b border-slate-100">
          <div>
            <h2 className="text-[15px] font-bold text-slate-800">Histórico</h2>
            <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[320px]">
              {client.nome || applyDocMask(client.documento)}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition">
            <IoMdClose size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          {isLoading ? (
            <div className="flex justify-center py-8 text-slate-400 text-sm gap-2">
              <svg className="animate-spin h-4 w-4 text-blue-500" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Carregando...
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-2 text-slate-400">
              <MdHistory size={32} className="opacity-25" />
              <p className="text-sm">Nenhuma entrada no histórico.</p>
            </div>
          ) : (
            entries.map((e) => (
              <div key={e.id} className="rounded-xl border border-slate-100 bg-slate-50 p-2">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{e.nota}</p>
                <div className="flex items-center justify-end mt-2.5 pt-2 border-t border-slate-100">
                  <span className="text-xs text-slate-400">
                    {new Date(e.createdAt).toLocaleString("pt-BR")}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const MonitoreClientesPage = () => {
  const queryClient = useQueryClient();
  const { user } = useApplication();
  const hasClientMonitoringAccess = hasTruthyFlag(
    user?.Company?.hasClientMonitoring
  );

  // ── Clientes ──────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [historyClient, setHistoryClient] = useState(null);
  const [nome, setNome] = useState("");
  const [documento, setDocumento] = useState("");
  const [docError, setDocError] = useState("");

  const { data: clientes = [], isLoading: loadingClientes } = useQuery(
    ["monitored-clients"],
    async () => {
      const { data } = await axios.get(MONITORED_CLIENT.LIST);
      return data?.data || [];
    },
    { enabled: hasClientMonitoringAccess }
  );

  const createClientMutation = useMutation(
    async (payload) => {
      const { data } = await axios.post(MONITORED_CLIENT.CREATE, payload);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["monitored-clients"]);
        setNome("");
        setDocumento("");
        setDocError("");
        setIsModalOpen(false);
      },
      onError: () => toast.error("Erro ao adicionar cliente."),
    }
  );

  const removeClientMutation = useMutation(
    async (id) => axios.delete(MONITORED_CLIENT.REMOVE(id)),
    {
      onSuccess: () => queryClient.invalidateQueries(["monitored-clients"]),
      onError: () => toast.error("Erro ao remover cliente."),
    }
  );

  const handleDocChange = (e) => {
    setDocumento(applyDocMask(e.target.value));
    setDocError("");
  };

  const handleAdd = () => {
    if (!hasClientMonitoringAccess) {
      toast.info("Solicite à equipe a liberação do Monitore Clientes.");
      return;
    }

    const digits = documento.replace(/\D/g, "");
    if (!validateDoc(digits)) {
      const tipo = digits.length <= 11 ? "CPF" : "CNPJ";
      setDocError(`${tipo} inválido. Verifique os dígitos informados.`);
      return;
    }
    createClientMutation.mutate({
      documento: digits,
      tipo: digits.length === 11 ? "CPF" : "CNPJ",
      nome: nome.trim() || null,
    });
  };

  // ── Carteira / E-mails ────────────────────────────────────────
  const [isCarteirModalOpen, setIsCarteiraModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [localEmails, setLocalEmails] = useState([]);

  const { isLoading: loadingEmails } = useQuery(
    ["monitored-client-emails"],
    async () => {
      const { data } = await axios.get(MONITORED_CLIENT.GET_EMAILS);
      return data?.data || [];
    },
    {
      enabled: isCarteirModalOpen,
      onSuccess: (data) => setLocalEmails(data.map((e) => e.email)),
    }
  );

  const saveEmailsMutation = useMutation(
    async (emails) => axios.put(MONITORED_CLIENT.SAVE_EMAILS, { emails }),
    {
      onSuccess: () => {
        toast.success("E-mails salvos com sucesso.");
        setIsCarteiraModalOpen(false);
        setEmailInput("");
        setEmailError("");
      },
      onError: () => toast.error("Erro ao salvar e-mails."),
    }
  );

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const handleAddEmail = () => {
    const val = emailInput.trim();
    if (!isValidEmail(val)) { setEmailError("Informe um e-mail válido."); return; }
    if (localEmails.includes(val)) { setEmailError("Este e-mail já foi adicionado."); return; }
    setLocalEmails((prev) => [...prev, val]);
    setEmailInput("");
    setEmailError("");
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); handleAddEmail(); }
  };

  const handleRemoveEmail = (email) => {
    setLocalEmails((prev) => prev.filter((e) => e !== email));
  };

  return (
    <div className="space-y-6">
      {/* Hero / Benefits */}
      <div className="rounded-2xl border border-slate-200/70 bg-gradient-to-br from-white via-slate-50 to-blue-50/30 shadow-sm px-4 py-3">
        <h1 className="text-2xl font-bold text-slate-800 leading-snug">
          Tenha o controle total sobre{" "}
          <span className="text-blue-600">quem você está negociando</span>
        </h1>
        <p className="mt-2 text-sm text-slate-500 leading-relaxed">
          Com o <strong className="text-slate-700">Monitore Clientes</strong>,
          você cadastra os CPFs e CNPJs dos seus parceiros, clientes ou
          fornecedores e passa a receber alertas automáticos sobre mudanças no
          perfil de crédito, novas negativações e eventos cadastrais relevantes.
          Reduza a inadimplência, proteja sua carteira e tome decisões com muito
          mais segurança.
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={b.title} className={`benefit-card ${b.color}`}>
                <span className="step-num">0{i + 1}</span>
                <div className={`benefit-icon ${b.color}`}>
                  <Icon size={17} />
                </div>
                <p className="benefit-title">{b.title}</p>
                <p className="benefit-desc">{b.desc}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Aviso período mínimo */}
      <div className="flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <MdWarningAmber size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-[12px] text-amber-800 leading-relaxed">
          <strong className="font-semibold">Período mínimo de 3 meses.</strong> Uma vez adicionado o
          cliente à carteira, o contrato de monitoramento tem vigência mínima de 3 meses. O cancelamento
          antes desse prazo{" "}
          <span className="font-semibold">não isenta a cobrança</span> — o valor integral dos 3 meses
          será cobrado normalmente.{" "}
          <span className="italic">Esta é uma política do birô de crédito, não da nossa plataforma.</span>
        </p>
      </div>

      {/* Clients table */}
      <div className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <p className="text-[15px] font-semibold text-slate-800">
              Clientes Monitorados
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {clientes.length} cliente{clientes.length !== 1 ? "s" : ""}{" "}
              cadastrado{clientes.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsCarteiraModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 active:scale-[0.97] transition-all px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm"
            >
              <MdManageAccounts size={18} />
              Gerenciar Carteira
            </button>
            <button
              onClick={() => {
                if (hasClientMonitoringAccess) setIsModalOpen(true);
              }}
              disabled={!hasClientMonitoringAccess}
              title={
                hasClientMonitoringAccess
                  ? "Adicionar cliente"
                  : "Solicite à equipe a liberação do Monitore Clientes."
              }
              className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold shadow-sm transition-all ${
                hasClientMonitoringAccess
                  ? "bg-blue-600 hover:bg-blue-700 active:scale-[0.97] text-white"
                  : "bg-slate-100 text-slate-400 cursor-not-allowed"
              }`}
            >
              <IoMdAdd size={18} />
              Adicionar Cliente
            </button>
          </div>
        </div>

        {!hasClientMonitoringAccess && (
          <div className="mx-5 mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Para adicionar clientes monitorados, solicite à equipe a liberação
            do Monitore Clientes para a sua empresa.
          </div>
        )}

        {loadingClientes ? (
          <div className="flex items-center justify-center py-12 text-slate-400 text-sm gap-2">
            <svg className="animate-spin h-4 w-4 text-blue-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            Carregando...
          </div>
        ) : clientes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-400">
            <MdRadar size={40} className="opacity-30" />
            <p className="text-sm">Nenhum cliente monitorado ainda.</p>
            <p className="text-xs text-slate-300">
              {hasClientMonitoringAccess
                ? "Clique em \"Adicionar Cliente\" para começar."
                : "Solicite à equipe a liberação para começar."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="px-5 py-3 font-semibold">Nome / Razão Social</th>
                  <th className="px-5 py-3 font-semibold">Documento</th>
                  <th className="px-5 py-3 font-semibold">Tipo</th>
                  <th className="px-5 py-3 font-semibold">Adicionado em</th>
                  <th className="px-5 py-3 font-semibold text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {clientes.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-slate-700">
                      {c.nome || "—"}
                    </td>
                    <td className="px-5 py-3.5 text-slate-600 font-mono">
                      {applyDocMask(c.documento)}
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                          c.tipo === "CPF"
                            ? "bg-blue-50 text-blue-700"
                            : "bg-violet-50 text-violet-700"
                        }`}
                      >
                        {c.tipo}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-400 text-xs">
                      {new Date(c.createdAt).toLocaleDateString("pt-BR")}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setHistoryClient(c)}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition"
                          title="Ver histórico"
                        >
                          <MdHistory size={13} />
                          Histórico
                        </button>
                        <button
                          onClick={() => removeClientMutation.mutate(c.id)}
                          disabled={removeClientMutation.isLoading}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-40"
                          title="Remover"
                        >
                          <IoMdTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Modal */}
      <Modal
        show={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setDocError("");
        }}
        size="md"
      >
        <Modal.Header className="py-2">
          <div className="text-base mt-1">
            Adicionar Cliente para Monitoramento
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nome / Razão Social{" "}
                <span className="text-slate-400 font-normal">(opcional)</span>
              </label>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: João Silva ou Empresa Ltda"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                CPF / CNPJ <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={documento}
                onChange={handleDocChange}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                maxLength={18}
                className={`w-full rounded-xl border ${
                  docError
                    ? "border-rose-300 bg-rose-50"
                    : "border-slate-200 bg-slate-50"
                } px-3.5 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition font-mono`}
              />
              {docError && (
                <p className="mt-1.5 text-xs text-rose-500">{docError}</p>
              )}
            </div>

            {/* O que será monitorado */}
            <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3">
              <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-widest mb-2.5">
                O que será monitorado
              </p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                {[
                  { icon: MdPersonSearch,         label: "Dados cadastrais" },
                  { icon: MdHistory,              label: "Histórico de consultas" },
                  { icon: MdAccountBalanceWallet, label: "Pendências financeiras" },
                  { icon: MdGavel,                label: "Protestos" },
                  { icon: MdMoneyOff,             label: "Cheques sem fundos" },
                  { icon: MdCreditScore,          label: "Crédito concedido" },
                  { icon: MdNotificationImportant,label: "Alerta de documento" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-md bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <Icon size={11} className="text-white" />
                    </div>
                    <span className="text-[11px] text-slate-600 leading-none">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="pt-2 pb-3">
          <div className="flex w-full justify-end gap-2">
            <button
              onClick={() => {
                setIsModalOpen(false);
                setDocError("");
              }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={handleAdd}
              disabled={createClientMutation.isLoading}
              className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-white transition"
            >
              <IoMdAdd size={16} />
              {createClientMutation.isLoading ? "Salvando..." : "Adicionar"}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal Histórico */}
      {historyClient && (
        <HistoryModal client={historyClient} onClose={() => setHistoryClient(null)} />
      )}

      {/* Modal Gerenciar Carteira */}
      <Modal
        show={isCarteirModalOpen}
        onClose={() => {
          setIsCarteiraModalOpen(false);
          setEmailInput("");
          setEmailError("");
        }}
        size="lg"
      >
        <Modal.Header className="py-2">
          <div className="text-base mt-1">Gerenciar Carteira</div>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-0.5">
                E-mails para notificação
              </p>
              <p className="text-xs text-slate-400 mb-3 leading-relaxed">
                Sempre que houver uma nova alteração em qualquer cliente monitorado,
                os e-mails abaixo receberão um alerta automático.
              </p>

              {/* Input + botão adicionar */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <MdEmail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); setEmailError(""); }}
                    onKeyDown={handleEmailKeyDown}
                    placeholder="exemplo@email.com"
                    className={`w-full rounded-xl border pl-8 pr-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                      emailError ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                </div>
                <button
                  onClick={handleAddEmail}
                  className="inline-flex items-center gap-1 rounded-xl bg-blue-600 hover:bg-blue-700 px-3 py-2 text-sm font-semibold text-white transition flex-shrink-0"
                >
                  <IoMdAdd size={16} />
                  Adicionar
                </button>
              </div>
              {emailError && (
                <p className="mt-1.5 text-xs text-rose-500">{emailError}</p>
              )}
            </div>

            {/* Lista de e-mails */}
            {loadingEmails ? (
              <div className="flex items-center justify-center py-6 text-slate-400 text-xs gap-2">
                <svg className="animate-spin h-3.5 w-3.5 text-blue-400" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Carregando e-mails...
              </div>
            ) : localEmails.length > 0 ? (
              <div className="rounded-xl border border-slate-100 bg-slate-50 divide-y divide-slate-100 overflow-hidden">
                {localEmails.map((email) => (
                  <div key={email} className="flex items-center justify-between px-3 py-2.5 gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <MdEmail size={12} className="text-blue-600" />
                      </div>
                      <span className="text-[13px] text-slate-700 truncate">{email}</span>
                    </div>
                    <button
                      onClick={() => handleRemoveEmail(email)}
                      className="w-6 h-6 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors flex-shrink-0"
                      title="Remover"
                    >
                      <IoMdClose size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 gap-1.5 text-slate-400 rounded-xl border border-dashed border-slate-200">
                <MdEmail size={22} className="opacity-30" />
                <p className="text-xs">Nenhum e-mail cadastrado ainda.</p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="pt-2 pb-3">
          <div className="flex w-full justify-end gap-2">
            <button
              onClick={() => { setIsCarteiraModalOpen(false); setEmailInput(""); setEmailError(""); }}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={() => saveEmailsMutation.mutate(localEmails)}
              disabled={saveEmailsMutation.isLoading}
              className="rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-white transition"
            >
              {saveEmailsMutation.isLoading ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MonitoreClientesPage;
